/**
 * TemplateEditor.tsx - 话术模板编辑器组件
 * BD可在此编辑各渠道各Phase的话术模板
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Space,
  Tag,
  message,
  Alert,
  Tooltip,
  Switch,
  Divider,
  Typography,
  Row,
  Col,
  Spin,
  Modal,
  Empty,
  Popconfirm,
} from 'antd'
import {
  SaveOutlined,
  CopyOutlined,
  EyeOutlined,
  UndoOutlined,
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
  BulbOutlined,
  RobotOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { userConfigApi } from '../api/client'
import type { UserConfig } from '../api/client'

const { TextArea } = Input
const { Text, Title, Paragraph } = Typography

// 渠道类型
type Channel = 'email' | 'twitter' | 'telegram'
type Phase = 1 | 2 | 3

// 模板变量定义
const templateVariables = [
  { name: '{project_name}', desc: '项目名称', example: 'Uniswap' },
  { name: '{project_website}', desc: '项目官网', example: 'https://uniswap.org' },
  { name: '{twitter_handle}', desc: 'Twitter账号', example: '@Uniswap' },
  { name: '{telegram_handle}', desc: 'Telegram用户名', example: '@uniswap_admin' },
  { name: '{project_category}', desc: '项目类别', example: 'DEX' },
  { name: '{project_chain}', desc: '所在链', example: 'Ethereum' },
  { name: '{project_tvl}', desc: 'TVL数值', example: '$5.2B' },
  { name: '{recent_event}', desc: '近期动态', example: '刚完成V4升级' },
  { name: '{sender_name}', desc: '发送者名称', example: 'Donald' },
  { name: '{sender_title}', desc: '发送者职位', example: 'BD Lead' },
  { name: '{company_name}', desc: '公司名称', example: 'TaskOn' },
  { name: '{date}', desc: '当前日期', example: '2026-02-03' },
]

// 默认模板内容
const defaultTemplates: Record<Channel, Record<Phase, { subject?: string; content: string }>> = {
  email: {
    1: {
      subject: 'Partnership Opportunity with {company_name}',
      content: `Hi {project_name} Team,

I'm {sender_name}, {sender_title} at {company_name}. I've been following {project_name}'s progress and was particularly impressed by {recent_event}.

We help Web3 projects like yours boost user engagement through gamified quest campaigns. Given {project_name}'s focus on {project_category}, I believe there's a great synergy opportunity.

Would you be open to a brief call this week to explore potential collaboration?

Best,
{sender_name}
{sender_title} | {company_name}`,
    },
    2: {
      subject: 'Following up - {company_name} x {project_name}',
      content: `Hi {project_name} Team,

Just wanted to follow up on my previous email about a potential partnership between {company_name} and {project_name}.

We've recently helped similar {project_category} projects achieve significant user growth. I'd love to share some case studies that might be relevant.

Would you have 15 minutes this week for a quick chat?

Best,
{sender_name}`,
    },
    3: {
      subject: 'Last follow-up - Partnership with {project_name}',
      content: `Hi {project_name} Team,

I wanted to reach out one more time regarding the collaboration opportunity between {company_name} and {project_name}.

I understand you might be busy, but I truly believe we can help {project_name} reach new users and boost engagement.

If now isn't the right time, please let me know and I'll follow up later.

Best regards,
{sender_name}`,
    },
  },
  twitter: {
    1: {
      content: `Hey {twitter_handle}! 👋 {sender_name} from {company_name} here. Impressed by what you're building in the {project_category} space. Would love to explore a potential collaboration - we help projects like yours boost user engagement through gamified quests. Mind if I share more details?`,
    },
    2: {
      content: `Hi {twitter_handle}, following up on my previous message! We've been helping {project_category} projects grow their communities significantly. Would be great to chat about how we could support {project_name}'s growth. Let me know if you're interested!`,
    },
    3: {
      content: `Hey {twitter_handle}, just wanted to check in one last time about the collaboration opportunity with {company_name}. We have some exciting new features that could benefit {project_name}. Feel free to reach out whenever you're ready to chat! 🚀`,
    },
  },
  telegram: {
    1: {
      content: `Hi! I'm {sender_name} from {company_name}. We help Web3 projects like {project_name} boost user engagement through gamified campaigns. I noticed your impressive work and would love to explore a potential partnership. Would you be open to a quick chat?`,
    },
    2: {
      content: `Hi, just following up on my previous message! We've helped similar projects achieve great results. I'd love to share some case studies with {project_name}. Let me know if you'd like to chat!`,
    },
    3: {
      content: `Hi, reaching out one more time about the potential collaboration between {company_name} and {project_name}. If now isn't the right time, no worries - just let me know and I'll follow up later. Thanks!`,
    },
  },
}

// 渠道配置
const channelConfig = {
  email: { icon: <MailOutlined />, label: 'Email', color: 'blue' },
  twitter: { icon: <TwitterOutlined />, label: 'Twitter DM', color: 'cyan' },
  telegram: { icon: <SendOutlined />, label: 'Telegram', color: 'purple' },
}

interface TemplateEditorProps {
  userConfig?: UserConfig
  onSave?: (templates: Record<string, unknown>) => void
}

export function TemplateEditor({ userConfig, onSave }: TemplateEditorProps) {
  const [form] = Form.useForm()
  const [activeChannel, setActiveChannel] = useState<Channel>('email')
  const [activePhase, setActivePhase] = useState<Phase>(1)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [templates, setTemplates] = useState<Record<string, Record<string, unknown>>>(
    userConfig?.templates || {}
  )
  const [previewVisible, setPreviewVisible] = useState(false)
  const [llmPolishing, setLlmPolishing] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // 获取当前模板
  const getCurrentTemplate = () => {
    const key = `${activeChannel}_phase${activePhase}`
    return templates[key] || getDefaultTemplate(activeChannel, activePhase)
  }

  // 获取默认模板
  const getDefaultTemplate = (channel: Channel, phase: Phase) => {
    return defaultTemplates[channel][phase]
  }

  // 加载模板到表单
  useEffect(() => {
    const template = getCurrentTemplate()
    form.setFieldsValue({
      subject: template.subject || '',
      content: template.content || '',
    })
  }, [activeChannel, activePhase, templates])

  // 保存模板
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const key = `${activeChannel}_phase${activePhase}`
      const newTemplates = {
        ...templates,
        [key]: {
          subject: values.subject,
          content: values.content,
          updated_at: new Date().toISOString(),
        },
      }

      await userConfigApi.updateTemplates(newTemplates)
      setTemplates(newTemplates)
      setHasChanges(false)
      message.success('模板保存成功')
      onSave?.(newTemplates)
    } catch (error: any) {
      message.error(error.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  // 恢复默认模板
  const handleResetToDefault = () => {
    const defaultTemplate = getDefaultTemplate(activeChannel, activePhase)
    form.setFieldsValue({
      subject: defaultTemplate.subject || '',
      content: defaultTemplate.content,
    })
    setHasChanges(true)
    message.info('已恢复为默认模板，请保存以生效')
  }

  // 插入变量
  const insertVariable = (variable: string) => {
    const currentContent = form.getFieldValue('content') || ''
    const textarea = document.querySelector('textarea[id="content"]') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newContent = currentContent.slice(0, start) + variable + currentContent.slice(end)
      form.setFieldValue('content', newContent)
      setHasChanges(true)
      // 设置光标位置
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + variable.length, start + variable.length)
      }, 0)
    } else {
      form.setFieldValue('content', currentContent + variable)
      setHasChanges(true)
    }
  }

  // 预览模板
  const renderPreview = () => {
    const content = form.getFieldValue('content') || ''
    const subject = form.getFieldValue('subject') || ''

    // 用示例数据替换变量
    const replaceVariables = (text: string) => {
      let result = text
      templateVariables.forEach((v) => {
        result = result.replace(new RegExp(v.name.replace(/[{}]/g, '\\$&'), 'g'), v.example)
      })
      return result
    }

    return (
      <div>
        {activeChannel === 'email' && subject && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>主题: </Text>
            <Text>{replaceVariables(subject)}</Text>
          </div>
        )}
        <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
            {replaceVariables(content)}
          </pre>
        </Card>
      </div>
    )
  }

  // LLM润色（模拟）
  const handleLlmPolish = async () => {
    if (!userConfig?.llm_enabled) {
      message.warning('请先在策略配置中启用并配置LLM')
      return
    }

    setLlmPolishing(true)
    try {
      // TODO: 实现实际的LLM润色API调用
      await new Promise((resolve) => setTimeout(resolve, 2000))
      message.info('LLM润色功能开发中...')
    } finally {
      setLlmPolishing(false)
    }
  }

  return (
    <div className="template-editor">
      {/* 渠道选择 */}
      <Tabs
        activeKey={activeChannel}
        onChange={(key) => setActiveChannel(key as Channel)}
        items={Object.entries(channelConfig).map(([key, config]) => ({
          key,
          label: (
            <Space>
              {config.icon}
              {config.label}
            </Space>
          ),
        }))}
      />

      {/* Phase选择 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Text strong>阶段: </Text>
          {([1, 2, 3] as Phase[]).map((phase) => (
            <Tag
              key={phase}
              color={activePhase === phase ? 'blue' : 'default'}
              style={{ cursor: 'pointer', padding: '4px 12px' }}
              onClick={() => setActivePhase(phase)}
            >
              Phase {phase}
            </Tag>
          ))}
        </Space>
      </div>

      {/* 变量列表 */}
      <Alert
        message="可用变量 (点击插入)"
        description={
          <Space wrap size={[8, 8]} style={{ marginTop: 8 }}>
            {templateVariables.map((v) => (
              <Tooltip key={v.name} title={`${v.desc} - 例: ${v.example}`}>
                <Tag
                  style={{ cursor: 'pointer' }}
                  onClick={() => insertVariable(v.name)}
                >
                  {v.name}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        }
        type="info"
        showIcon
        icon={<BulbOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* 模板表单 */}
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => setHasChanges(true)}
      >
        {activeChannel === 'email' && (
          <Form.Item
            name="subject"
            label={
              <Space>
                <span>邮件主题</span>
                <Tooltip title="支持使用变量，每次发送时会随机选择一个主题（多个主题用换行分隔）">
                  <BulbOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: '请输入邮件主题' }]}
          >
            <TextArea
              rows={2}
              placeholder="Partnership Opportunity with {company_name}&#10;Collaboration proposal for {project_name}"
            />
          </Form.Item>
        )}

        <Form.Item
          name="content"
          label={
            <Space>
              <span>模板内容</span>
              <Tag color="blue">{channelConfig[activeChannel].label}</Tag>
              <Tag>Phase {activePhase}</Tag>
            </Space>
          }
          rules={[{ required: true, message: '请输入模板内容' }]}
        >
          <TextArea
            id="content"
            rows={activeChannel === 'email' ? 15 : 8}
            placeholder="在此输入模板内容，可使用上方的变量..."
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>
      </Form>

      <Divider />

      {/* 操作按钮 */}
      <Row justify="space-between" align="middle">
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              保存模板
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => setPreviewVisible(true)}
            >
              预览
            </Button>
            <Popconfirm
              title="确定恢复默认模板？"
              description="当前编辑内容将丢失"
              onConfirm={handleResetToDefault}
            >
              <Button icon={<UndoOutlined />}>恢复默认</Button>
            </Popconfirm>
          </Space>
        </Col>
        <Col>
          <Tooltip title={userConfig?.llm_enabled ? 'AI优化模板内容' : '需要先配置LLM'}>
            <Button
              icon={<RobotOutlined />}
              onClick={handleLlmPolish}
              loading={llmPolishing}
              disabled={!userConfig?.llm_enabled}
            >
              AI润色
            </Button>
          </Tooltip>
        </Col>
      </Row>

      {/* 预览弹窗 */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            模板预览
            <Tag color={channelConfig[activeChannel].color}>
              {channelConfig[activeChannel].label}
            </Tag>
            <Tag>Phase {activePhase}</Tag>
          </Space>
        }
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={
          <Button onClick={() => setPreviewVisible(false)}>关闭</Button>
        }
        width={700}
      >
        <Alert
          message="预览使用示例数据渲染"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {renderPreview()}
      </Modal>

      {hasChanges && (
        <Alert
          message="有未保存的更改"
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  )
}

// 独立的模板编辑页面包装器
export function TemplateEditorPage() {
  const [loading, setLoading] = useState(true)
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null)

  useEffect(() => {
    loadUserConfig()
  }, [])

  const loadUserConfig = async () => {
    try {
      const config = await userConfigApi.get()
      setUserConfig(config)
    } catch (error) {
      message.error('加载配置失败')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>加载配置中...</div>
        </div>
      </Card>
    )
  }

  return (
    <Card title="话术模板编辑器">
      <TemplateEditor
        userConfig={userConfig || undefined}
        onSave={() => loadUserConfig()}
      />
    </Card>
  )
}

export default TemplateEditor
